import { Box, Flex } from "@rebass/grid";
import {
  EApplicationFilter,
  IFilterCriteria,
  TPetsCriterion,
  TStartDateDirection,
} from "@rentiohq/shared-frontend/dist/reduxV2/applicationFilters/types";
import {
  getLocale,
  getLocalizedText,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  DropdownMenu,
  Select,
  Icon,
} from "@rentiohq/web-shared/dist/components";
import { BaseDatePicker } from "@rentiohq/web-shared/dist/components/BaseDatePicker";
import { StyledBackdrop } from "@rentiohq/web-shared/dist/components/TextField/TextField.styled";
import { ts } from "@rentiohq/web-shared/dist/services";
import { Rating } from "components/Rating";
import React from "react";
import ReactDatePicker from "react-datepicker";
import styled from "styled-components";
import {
  ALL_APPLICATION_FILTERS,
  getDefaultStartDate,
  toFilterDay,
} from "../../applicationFilters.utils";
import { RM_COLORS } from "../../rentmatch.colors";

interface IProps {
  filterCriteria: IFilterCriteria;
  onChange: (criteria: IFilterCriteria) => void;
  /** The filters the panel shows, in any order; rendered in the fixed one. */
  visibleFilters: EApplicationFilter[];
  onToggleFilter: (filter: EApplicationFilter) => void;
  /** The property's availability date, where the start date filter starts. */
  availableAtDate?: string | Date;
  onSave: () => void;
  onClear: () => void;
  onClose?: () => void;
  isEditing?: boolean;
  activeFilterName?: string;
}

// Every filter has one header line above one control row of the same height,
// so the filters in a row line up whatever their width (DEV-3543).
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${RM_COLORS.grayMutedAlt};
  text-transform: uppercase;
  margin-bottom: 12px;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

// The value and a hidden widest value share one grid cell, so the header keeps
// the width of "≥ 100%" while the slider moves and the filter never resizes.
const HeaderValue = styled.span`
  display: inline-grid;
  justify-items: end;
  color: ${RM_COLORS.slate};
  text-transform: none;

  > * {
    grid-area: 1 / 1;
  }
`;

const HiddenWidth = styled.span`
  visibility: hidden;
`;

const Control = styled.div`
  display: flex;
  align-items: center;
  min-height: 48px;

  > * {
    flex: 1;
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: ${RM_COLORS.slate};
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    margin: 2px 0 0;
    flex-shrink: 0;
    accent-color: ${RM_COLORS.rentmatchGreen};
    cursor: pointer;
  }
`;

// The first checkbox lines up with the fields of its row; the rest follow below.
const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  min-height: 48px;
`;

const AddFilterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  min-width: 240px;
`;

// The theme's outline button in Rentmatch green on a white fill, so it reads as
// the secondary action beside "Save".
const AddFilterTrigger = styled.div`
  button,
  button:hover,
  button:focus,
  button:active {
    background: white;
    border-color: ${RM_COLORS.rentmatchGreen};
    color: ${RM_COLORS.rentmatchGreen};
    white-space: nowrap;
  }
`;

// The date stays disabled until a direction is picked; it keeps the white fill
// of the select beside it, so the two read as one field style.
const DateField = styled.div`
  && ${StyledBackdrop} {
    background-color: white;
  }
`;

const DATE_FORMAT = "dd/MM/yyyy";

const FILTER_TITLE_KEYS: Record<EApplicationFilter, string> = {
  [EApplicationFilter.Income]: "application.filters.panel.income.title",
  [EApplicationFilter.RentMatchScore]:
    "application.filters.panel.rentmatch.title",
  [EApplicationFilter.Occupants]: "application.filters.panel.occupants.title",
  [EApplicationFilter.Applications]:
    "application.filters.panel.applications.title",
  [EApplicationFilter.Rating]: "application.filters.panel.rating.title",
  [EApplicationFilter.Pets]: "application.filters.panel.pets.title",
  [EApplicationFilter.StartDate]: "application.filters.panel.start_date.title",
  [EApplicationFilter.DocumentsComplete]:
    "application.filters.panel.documents.title",
  [EApplicationFilter.NoNoShowsElsewhere]:
    "application.filters.panel.no_shows.title",
};

const SELECT_WIDTH = 150;
const SELECT_MAX_WIDTH = 180;
const FILTER_MAX_WIDTH = 320;
const COUNT_SELECT_WIDTH = 80;

// Each filter is as wide as its texts need in the current language, at least
// as wide as its fields and at most FILTER_MAX_WIDTH, beyond which its texts
// wrap; the panel wraps onto extra rows when the filters do not fit (DEV-3543).
const FilterColumn = styled.div<{ $fieldWidth?: number }>`
  flex: 0 0 auto;
  min-width: ${({ $fieldWidth = 0 }) => $fieldWidth + 32}px;
  max-width: ${FILTER_MAX_WIDTH + 32}px;
  padding: 0 16px;
  margin-bottom: 32px;
`;

/**
 * One of two fields in a filter: its field's width, or its header's when that
 * is wider. A set width rather than a minimum, because a date input asks for
 * far more room than it needs and would push into the next filter.
 */
const FilterField = styled.div<{ $fieldWidth: number }>`
  flex: 1 0 auto;
  width: ${({ $fieldWidth }) => $fieldWidth + 16}px;
  padding: 0 8px;
`;

// A select's width follows its longest option in the current language, never
// the option picked: the hidden list of every label sets the width, and the
// select itself is kept out of the sizing, so picking "4x rent or more" does
// not widen the filter and push another one onto the next row. Past
// SELECT_MAX_WIDTH a long option is cut short in the field, not in the menu.
const SelectWidth = styled.div`
  > :first-child {
    contain: inline-size;
  }
`;

const OptionWidths = styled.div`
  height: 0;
  overflow: hidden;
  visibility: hidden;
  white-space: nowrap;
  font-size: 16px;
  padding: 0 56px 0 18px;
  max-width: ${SELECT_MAX_WIDTH}px;
`;

type TFilterSelectProps = Omit<React.ComponentProps<typeof Select>, "label">;

const FilterSelect = (props: TFilterSelectProps) => {
  const options = (props.options || []) as { label: string }[];

  return (
    <SelectWidth>
      <Select
        {...props}
        label=""
        labelHidden={true}
        menuPosition="fixed"
        menuPortalTarget={document.body}
      />
      <OptionWidths aria-hidden={true}>
        {options.map(option => (
          <div key={option.label}>{option.label}</div>
        ))}
      </OptionWidths>
    </SelectWidth>
  );
};

const APPLICATION_COUNT_OPTIONS = [1, 2, 3, 4, 5];

type TApplicationsDirection = "moreThan" | "atMost";

/** "yyyy-MM-dd" as a local calendar day, the way the picker shows it. */
const parseFilterDay = (day?: string) => {
  const [year, month, date] = (day || "").split("-").map(Number);
  return year && month && date ? new Date(year, month - 1, date) : null;
};

const StyledSlider = styled.input<{ percentage: number }>`
  -webkit-appearance: none;
  width: 100%;
  height: 6px;
  background: linear-gradient(
    to right,
    ${RM_COLORS.borderAlt} ${props => props.percentage}%,
    ${RM_COLORS.borderAlt} ${props => props.percentage}%
  );
  border-radius: 4px;
  outline: none;
  margin-top: 10px;
  margin-bottom: 8px;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${RM_COLORS.rentmatchGreen};
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

export const ApplicationsFilterPanel: React.FC<IProps> = ({
  filterCriteria,
  onChange,
  visibleFilters,
  onToggleFilter,
  availableAtDate,
  onSave,
  onClear,
  onClose,
  isEditing,
  activeFilterName,
}) => {
  const datePickerRef = React.useRef<ReactDatePicker>(null);

  const handleChange = (field: keyof IFilterCriteria, value: any) => {
    onChange({ ...filterCriteria, [field]: value });
  };

  // "More than N" keeps living in `minApplications`, so saved filters from
  // before DEV-3543 mean what they meant; "at most N" is its own field. With
  // no count picked yet neither field says which way, so the picked direction
  // is held here; otherwise "at most" would snap back to "more than".
  const [pickedApplicationsDirection, setPickedApplicationsDirection] =
    React.useState<TApplicationsDirection>("moreThan");
  const getApplicationsDirection = (): TApplicationsDirection => {
    if (filterCriteria.maxApplications != null) {
      return "atMost";
    }
    if (filterCriteria.minApplications != null) {
      return "moreThan";
    }
    return pickedApplicationsDirection;
  };
  const applicationsDirection = getApplicationsDirection();
  const applicationsCount =
    filterCriteria.maxApplications ?? filterCriteria.minApplications;

  const handleApplicationsChange = (
    direction: TApplicationsDirection,
    count?: number,
  ) => {
    setPickedApplicationsDirection(direction);
    onChange({
      ...filterCriteria,
      minApplications: direction === "moreThan" ? count : undefined,
      maxApplications: direction === "atMost" ? count : undefined,
    });
  };

  // The date is only picked once a direction is, and starts on the property's
  // availability date, so turning the filter on never filters by itself.
  const handleStartDateDirectionChange = (direction?: TStartDateDirection) => {
    onChange({
      ...filterCriteria,
      startDateDirection: direction,
      startDate: direction
        ? filterCriteria.startDate || getDefaultStartDate(availableAtDate)
        : undefined,
    });
  };

  const isVisible = (filter: EApplicationFilter) =>
    visibleFilters.includes(filter);

  const renderAddFilter = () => (
    <DropdownMenu
      passDownClickEvents={true}
      shouldCloseOnClick={false}
      // The trigger sits at the panel's bottom edge: open upward, and let the
      // popper flip down when there is no room above.
      triggerProps={{ placement: "top-start" }}
      renderContent={() => (
        <AddFilterList>
          {ALL_APPLICATION_FILTERS.map(filter => (
            <CheckboxRow key={filter}>
              <input
                type="checkbox"
                checked={isVisible(filter)}
                onChange={() => onToggleFilter(filter)}
              />
              {getLocalizedText(FILTER_TITLE_KEYS[filter])}
            </CheckboxRow>
          ))}
        </AddFilterList>
      )}
    >
      <AddFilterTrigger>
        <Button appearance="outline">
          {getLocalizedText("application.filters.panel.add_filter")}
        </Button>
      </AddFilterTrigger>
    </DropdownMenu>
  );

  return (
    <Box
      p={4}
      // Negative horizontal margin cancels the parent Card's padding so the
      // panel background spans the full width edge-to-edge (no white gutters).
      mx={-4}
      bg={RM_COLORS.gray50Alt}
      style={{ borderBottom: `1px solid ${RM_COLORS.borderAlt}` }}
    >
      <Flex
        mb={3}
        justifyContent="space-between"
        alignItems="center"
        style={{ height: "30px" }}
      >
        <Box
          fontSize={16}
          color={RM_COLORS.slate}
          style={{
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
            marginRight: "16px",
          }}
        >
          {activeFilterName ||
            getLocalizedText("application.filters.panel.title")}
        </Box>
        {onClose && (
          <Box
            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            onClick={onClose}
          >
            <Icon source="close" size="medium" />
          </Box>
        )}
      </Flex>

      {visibleFilters.length === 0 && (
        <Box fontSize={14} color={RM_COLORS.slate} mb={4}>
          {getLocalizedText("application.filters.panel.add_filter.empty")}
        </Box>
      )}

      <Flex mx={-3} flexWrap="wrap" alignItems="flex-start">
        {isVisible(EApplicationFilter.Income) && (
          <FilterColumn $fieldWidth={SELECT_WIDTH}>
            <SectionHeader>
              {getLocalizedText("application.filters.panel.income.title")}
            </SectionHeader>
            <Control>
              <FilterSelect
                id="incomeMultiplier"
                value={filterCriteria.incomeMultiplier?.toString() || ""}
                onChange={(option: any) =>
                  handleChange(
                    "incomeMultiplier",
                    option.value ? Number(option.value) : undefined,
                  )
                }
                options={[
                  {
                    value: "",
                    label: getLocalizedText(
                      "application.filters.panel.income.minimum",
                    ),
                  },
                  {
                    value: "1",
                    label: getLocalizedText(
                      "application.filters.panel.income.1x",
                    ),
                  },
                  {
                    value: "2",
                    label: getLocalizedText(
                      "application.filters.panel.income.2x",
                    ),
                  },
                  {
                    value: "3",
                    label: getLocalizedText(
                      "application.filters.panel.income.3x",
                    ),
                  },
                  {
                    value: "4",
                    label: getLocalizedText(
                      "application.filters.panel.income.4x",
                    ),
                  },
                ]}
              />
            </Control>
          </FilterColumn>
        )}

        {isVisible(EApplicationFilter.RentMatchScore) && (
          <FilterColumn $fieldWidth={220}>
            <SectionHeader>
              {getLocalizedText("application.filters.panel.rentmatch.title")}
              <HeaderValue>
                <HiddenWidth aria-hidden={true}>≥ 100%</HiddenWidth>
                <span>{`≥ ${filterCriteria.rentMatchMinScore || 0}%`}</span>
              </HeaderValue>
            </SectionHeader>
            <Control>
              <Box>
                <StyledSlider
                  type="range"
                  min="0"
                  max="100"
                  value={filterCriteria.rentMatchMinScore || 0}
                  onChange={e =>
                    handleChange("rentMatchMinScore", Number(e.target.value))
                  }
                  percentage={filterCriteria.rentMatchMinScore || 0}
                />
                <Flex
                  justifyContent="space-between"
                  fontSize={12}
                  style={{ fontWeight: 600 }}
                >
                  <Box color={RM_COLORS.slate}>0%</Box>
                  <Box color={RM_COLORS.slate}>100%</Box>
                </Flex>
              </Box>
            </Control>
          </FilterColumn>
        )}

        {isVisible(EApplicationFilter.Occupants) && (
          <FilterColumn>
            <Flex mx={-2}>
              <FilterField $fieldWidth={SELECT_WIDTH}>
                <SectionHeader>
                  {getLocalizedText(
                    "application.filters.panel.occupants.adults",
                  )}
                </SectionHeader>
                <FilterSelect
                  id="maxAdults"
                  value={filterCriteria.maxAdults?.toString() || ""}
                  onChange={(option: any) =>
                    handleChange(
                      "maxAdults",
                      option.value ? Number(option.value) : undefined,
                    )
                  }
                  options={[
                    { value: "", label: "-" },
                    { value: "1", label: "1" },
                    { value: "2", label: "2" },
                    { value: "3", label: "3" },
                    { value: "4", label: "4" },
                    { value: "5", label: "5" },
                  ]}
                />
              </FilterField>
              <FilterField $fieldWidth={SELECT_WIDTH}>
                <SectionHeader>
                  {getLocalizedText(
                    "application.filters.panel.occupants.children",
                  )}
                </SectionHeader>
                <FilterSelect
                  id="maxChildren"
                  value={filterCriteria.maxChildren?.toString() || ""}
                  onChange={(option: any) =>
                    handleChange(
                      "maxChildren",
                      option.value ? Number(option.value) : undefined,
                    )
                  }
                  options={[
                    { value: "", label: "-" },
                    { value: "0", label: "0" },
                    { value: "1", label: "1" },
                    { value: "2", label: "2" },
                    { value: "3", label: "3" },
                    { value: "4", label: "4" },
                    { value: "5", label: "5" },
                  ]}
                />
              </FilterField>
            </Flex>
          </FilterColumn>
        )}

        {isVisible(EApplicationFilter.Rating) && (
          <FilterColumn $fieldWidth={150}>
            <SectionHeader>
              {getLocalizedText("application.filters.panel.rating.title")}
            </SectionHeader>
            <Control>
              <Rating
                value={filterCriteria.minRating || 0}
                onChange={(val: number) => handleChange("minRating", val)}
                size={24}
              />
            </Control>
          </FilterColumn>
        )}

        {isVisible(EApplicationFilter.Applications) && (
          <FilterColumn>
            <SectionHeader>
              {getLocalizedText("application.filters.panel.applications.title")}
            </SectionHeader>
            <Flex mx={-2}>
              <FilterField $fieldWidth={SELECT_WIDTH}>
                <FilterSelect
                  id="applicationsDirection"
                  value={applicationsDirection}
                  onChange={(option: any) =>
                    handleApplicationsChange(option.value, applicationsCount)
                  }
                  options={[
                    {
                      value: "moreThan",
                      label: getLocalizedText(
                        "application.filters.panel.applications.more_than",
                      ),
                    },
                    {
                      value: "atMost",
                      label: getLocalizedText(
                        "application.filters.panel.applications.at_most",
                      ),
                    },
                  ]}
                />
              </FilterField>
              <FilterField $fieldWidth={COUNT_SELECT_WIDTH}>
                <FilterSelect
                  id="applicationsCount"
                  value={applicationsCount?.toString() || ""}
                  onChange={(option: any) =>
                    handleApplicationsChange(
                      applicationsDirection,
                      option.value ? Number(option.value) : undefined,
                    )
                  }
                  options={[
                    { value: "", label: "-" },
                    ...APPLICATION_COUNT_OPTIONS.map(count => ({
                      value: count.toString(),
                      label: count.toString(),
                    })),
                  ]}
                />
              </FilterField>
            </Flex>
          </FilterColumn>
        )}

        {isVisible(EApplicationFilter.Pets) && (
          <FilterColumn $fieldWidth={SELECT_WIDTH}>
            <SectionHeader>
              {getLocalizedText("application.filters.panel.pets.title")}
            </SectionHeader>
            <Control>
              <FilterSelect
                id="pets"
                value={filterCriteria.pets || ""}
                onChange={(option: any) =>
                  handleChange(
                    "pets",
                    (option.value as TPetsCriterion) || undefined,
                  )
                }
                options={[
                  { value: "", label: "-" },
                  {
                    value: "some",
                    label: getLocalizedText(
                      "application.filters.panel.pets.some",
                    ),
                  },
                  {
                    value: "none",
                    label: getLocalizedText(
                      "application.filters.panel.pets.none",
                    ),
                  },
                ]}
              />
            </Control>
          </FilterColumn>
        )}

        {isVisible(EApplicationFilter.StartDate) && (
          <FilterColumn>
            <SectionHeader>
              {getLocalizedText("application.filters.panel.start_date.title")}
            </SectionHeader>
            <Flex mx={-2}>
              <FilterField $fieldWidth={SELECT_WIDTH}>
                <FilterSelect
                  id="startDateDirection"
                  value={filterCriteria.startDateDirection || ""}
                  onChange={(option: any) =>
                    handleStartDateDirectionChange(
                      (option.value as TStartDateDirection) || undefined,
                    )
                  }
                  options={[
                    { value: "", label: "-" },
                    {
                      value: "onOrBefore",
                      label: getLocalizedText(
                        "application.filters.panel.start_date.on_or_before",
                      ),
                    },
                    {
                      value: "from",
                      label: getLocalizedText(
                        "application.filters.panel.start_date.from",
                      ),
                    },
                  ]}
                />
              </FilterField>
              <FilterField $fieldWidth={SELECT_WIDTH}>
                <DateField>
                  <BaseDatePicker
                    id="startDate"
                    ref={datePickerRef}
                    placeholderText={ts.datePlaceholder(DATE_FORMAT)}
                    locale={getLocale()}
                    selected={parseFilterDay(filterCriteria.startDate)}
                    onChange={(input: Date | null) =>
                      handleChange(
                        "startDate",
                        input
                          ? toFilterDay(input)
                          : getDefaultStartDate(availableAtDate),
                      )
                    }
                    dateFormat={DATE_FORMAT}
                    showTimeSelect={false}
                    hideDayPicker={false}
                    hasError={false}
                    isDisabled={!filterCriteria.startDateDirection}
                    required={true}
                    inverseYears={false}
                    handleClear={() =>
                      handleChange(
                        "startDate",
                        getDefaultStartDate(availableAtDate),
                      )
                    }
                    handleIconClick={() => datePickerRef.current?.setOpen(true)}
                  />
                </DateField>
              </FilterField>
            </Flex>
          </FilterColumn>
        )}

        {/* The two checkbox filters share one column under one header: the
            first sits on the field line of its row, the second below it. */}
        {(isVisible(EApplicationFilter.DocumentsComplete) ||
          isVisible(EApplicationFilter.NoNoShowsElsewhere)) && (
          <FilterColumn>
            <SectionHeader>
              {getLocalizedText("application.filters.panel.show.title")}
            </SectionHeader>
            <CheckboxList>
              {isVisible(EApplicationFilter.DocumentsComplete) && (
                <CheckboxRow>
                  <input
                    type="checkbox"
                    checked={!!filterCriteria.documentsComplete}
                    onChange={e =>
                      handleChange(
                        "documentsComplete",
                        e.target.checked || undefined,
                      )
                    }
                  />
                  {getLocalizedText(
                    "application.filters.panel.documents.complete",
                  )}
                </CheckboxRow>
              )}
              {isVisible(EApplicationFilter.NoNoShowsElsewhere) && (
                <CheckboxRow>
                  <input
                    type="checkbox"
                    checked={!!filterCriteria.noNoShowsElsewhere}
                    onChange={e =>
                      handleChange(
                        "noNoShowsElsewhere",
                        e.target.checked || undefined,
                      )
                    }
                  />
                  {getLocalizedText("application.filters.panel.no_shows.hide")}
                </CheckboxRow>
              )}
            </CheckboxList>
          </FilterColumn>
        )}
      </Flex>

      <Flex alignItems="center">
        {renderAddFilter()}
        <Box
          ml="auto"
          mr={3}
          style={{
            cursor: "pointer",
            color: RM_COLORS.rentmatchGreen,
            fontSize: 14,
            fontWeight: 600,
          }}
          onClick={onClear}
        >
          {getLocalizedText("application.filters.panel.clear")}
        </Box>
        <Button appearance="primary" onClick={onSave}>
          {isEditing
            ? getLocalizedText("application.filters.panel.update")
            : getLocalizedText("application.filters.panel.save")}
        </Button>
      </Flex>
    </Box>
  );
};
